// Mirror of popinwebapp/resources/js/bootstrap.js — exposes the globals that
// the legacy components rely on (window.axios, window._, window.moment, jQuery).
//
// Vite ESM equivalents of the original CommonJS requires.

import axios from 'axios'
import _ from 'lodash'
import moment from 'moment'
import $ from 'jquery'
import { Capacitor } from '@capacitor/core'

window._ = _
window.moment = moment
window.$ = window.jQuery = $
window.axios = axios

// ── Capacitor / native app detection ─────────────────────────────────────────
//
//    Use the official @capacitor/core import (more reliable than window.Capacitor
//    which may not be initialised yet at module-load time).
//
//    server.hostname in capacitor.config.ts makes the WebView THINK its origin
//    is https://popinweddingdesigner.com, but the assets are loaded from local
//    APK files.  Relative /api/* requests therefore resolve to:
//      https://popinweddingdesigner.com/api/*
//    which is exactly what we want — same-origin, no CORS.
//
//    We still set baseURL explicitly so any code that uses window.axios directly
//    (legacy components) also hits the right host.
const isNative = Capacitor.isNativePlatform()
const apiHost = import.meta.env.VITE_LARAVEL_HOST || 'https://popinweddingdesigner.com'

// On Android the WebView's apparent origin is already popinweddingdesigner.com
// so relative paths resolve correctly. In the browser we rely on the Vite dev
// proxy (see vite.config.js) which forwards /api/* and /admin/* server-side —
// no CORS preflight, no wildcard conflict.
if (isNative) {
  axios.defaults.baseURL = apiHost
}

// Make the SPA look same-origin to the Laravel session/CSRF stack.
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

// withCredentials sends session cookies for Laravel web-session auth.
// On native, CapacitorHttp + CapacitorCookies bypass CORS entirely so
// withCredentials: true is safe and required for XSRF-TOKEN to work on iOS.
axios.defaults.withCredentials = true

// CSRF: if a <meta name="csrf-token"> tag is present (set by index.html or
// rewritten after login), thread its value into every write.
const csrfMeta = document.head.querySelector('meta[name="csrf-token"]')
if (csrfMeta && csrfMeta.content) {
  axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfMeta.content
}

// Read a cookie value from document.cookie (CapacitorCookies syncs the
// native URLSession jar back to document.cookie so this works on iOS too).
function readCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

// Silently refresh the Laravel CSRF cookie so the XSRF-TOKEN is current.
async function refreshCsrf() {
  try { await axios.get('/sanctum/csrf-cookie') } catch (_) {}
}

// userid injection: /api/* endpoints identify the user via a `userid`
// query/body param (not the Passport token). Auto-attach when we have one.
// Legacy /admin/* routes are session-authenticated — they don't accept
// a userid param and injecting one causes 500 errors from those controllers.
axios.interceptors.request.use((cfg) => {
  // iOS fix: WKWebView / URLSession (used by CapacitorHttp) reject unencoded
  // JSON ("{...}") in a query string, so the many `?filter=<JSON>` GETs across
  // the legacy components silently fail on iOS (popups open with no data),
  // while Android's Chromium WebView tolerates it. Percent-encode any raw
  // filter JSON before the request leaves. Harmless on web/Android — Laravel
  // decodes the param identically — and a no-op if it's already encoded
  // (encoded values contain %7B, not a literal "{", so the pattern won't match).
  if (cfg.url) {
    cfg.url = cfg.url.replace(
      /([?&]filter=)(\{[^&]*\})/g,
      (_m, prefix, json) => prefix + encodeURIComponent(json)
    )
  }

  // iOS CSRF fix: axios's built-in xsrf reading doesn't fire reliably inside
  // WKWebView. Read the XSRF-TOKEN cookie manually and set X-XSRF-TOKEN on
  // every write request so Laravel's VerifyCsrfToken middleware accepts it.
  const method = (cfg.method || 'get').toLowerCase()
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const xsrfToken = readCookie('XSRF-TOKEN')
    if (xsrfToken && !cfg.headers['X-XSRF-TOKEN']) {
      cfg.headers['X-XSRF-TOKEN'] = xsrfToken
    }
  }

  const userid = localStorage.getItem('popin_userid')
  const token = localStorage.getItem(import.meta.env.VITE_TOKEN_STORAGE_KEY || 'popin_token')
  if (token && !cfg.headers.Authorization) {
    cfg.headers.Authorization = `Bearer ${token}`
  }
  const url = cfg.url || ''
  const isApiRoute = url.startsWith('/api/') || url.startsWith('api/')

  // Branch context for stateless /api/admin/* routes: the legacy Admin\*
  // controllers read loc/cocode/abr from the session, which AdminApiContext
  // seeds (in priority order) from X-Loc/X-Cocode/X-Abr headers. The session
  // cookie isn't reliably sent on iOS WKWebView / native / cross-site, and
  // many components (e.g. booking.vue) send a `filter` that omits these — so
  // attach them from the stored login session on every API request.
  if (isApiRoute) {
    try {
      const session = JSON.parse(localStorage.getItem('popin_session') || 'null')
      if (session) {
        const cocode = session.cocode || session.Code
        const loc = session.loc || session.branchcode || session.Branch_Code
        const abr = session.abr
        if (loc && !cfg.headers['X-Loc']) cfg.headers['X-Loc'] = loc
        if (cocode && !cfg.headers['X-Cocode']) cfg.headers['X-Cocode'] = cocode
        if (abr && !cfg.headers['X-Abr']) cfg.headers['X-Abr'] = abr
      }
    } catch (_) { /* malformed session — skip */ }
  }

  if (userid && isApiRoute) {
    cfg.params = cfg.params ?? {}
    if (!('userid' in cfg.params)) cfg.params.userid = userid
    if (
      (method === 'post' || method === 'put' || method === 'patch') &&
      cfg.data &&
      typeof cfg.data === 'object' &&
      !Array.isArray(cfg.data) &&
      !(cfg.data instanceof FormData) &&
      !('userid' in cfg.data)
    ) {
      cfg.data.userid = userid
    }
  }

  // Log the full direct URL this request resolves to (apiHost + path + params).
  const qs = cfg.params
    ? new URLSearchParams(cfg.params).toString()
    : ''
  console.log('[API]', `${apiHost}${url}${qs ? `?${qs}` : ''}`)

  return cfg
})

// Duplicate-save guard for bookings / sales / events / receipts / payments.
// While a save to one of these endpoints is in flight, any second save to the
// same endpoint (double-click, double-tap, re-entrant handler) is cancelled
// before it leaves the browser. The backend's PreventDuplicateSubmission
// middleware is the real guarantee; this just stops the extra request early.
const SAVE_ENDPOINTS = /\/api\/admin\/(billssave|salessave|eventssave|receiptssave|paymentssave|receiptssalessave|sale\/paymentssave|event\/receiptssave|event\/paymentssave)(\?|$)/
const savesInFlight = new Set()

function saveKey(cfg) {
  const url = (cfg.url || '').replace(/^https?:\/\/[^/]+/, '')
  return SAVE_ENDPOINTS.test(url) ? url.split('?')[0] : null
}

axios.interceptors.request.use((cfg) => {
  if ((cfg.method || 'get').toLowerCase() !== 'post') return cfg
  const key = saveKey(cfg)
  if (!key) return cfg
  if (savesInFlight.has(key)) {
    const err = new axios.CanceledError('Duplicate save blocked — previous save still in progress.')
    err.duplicate = true
    return Promise.reject(err)
  }
  savesInFlight.add(key)
  cfg._saveKey = key
  return cfg
})

function releaseSave(cfg) {
  if (cfg && cfg._saveKey) savesInFlight.delete(cfg._saveKey)
}

axios.interceptors.response.use(
  (res) => { releaseSave(res.config); return res },
  (error) => {
    releaseSave(error && error.config)
    if (error?.response?.status === 409 && error.response.data?.duplicate) {
      error.duplicate = true
      const vm = window.__vueRoot__
      const text = error.response.data.message || 'Duplicate save blocked.'
      if (vm && vm.$fire) vm.$fire({ title: 'Duplicate', text, type: 'warning', timer: 3000 })
    }
    return Promise.reject(error)
  }
)

// 419 auto-retry: if the server rejects with CSRF mismatch, refresh the
// CSRF cookie and retry the request once. Covers expired tokens on iOS.
axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    if (error?.response?.status === 419 && !error.config._csrfRetry) {
      error.config._csrfRetry = true
      await refreshCsrf()
      const xsrfToken = readCookie('XSRF-TOKEN')
      if (xsrfToken) error.config.headers['X-XSRF-TOKEN'] = xsrfToken
      return axios(error.config)
    }
    return Promise.reject(error)
  }
)
